import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, Calendar, Tag, Share2, Twitter, Linkedin, Facebook, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { useBlogs } from "@/hooks/usePortfolio";
import { getImageUrl } from "@/api/client";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const renderContent = (content) => {
  if (!content) return null;
  const blocks = content.split("\n\n");
  return blocks.map((blk, i) => {
    if (blk.startsWith("## ")) return <h2 key={i}>{blk.replace(/^##\s*/, "")}</h2>;
    if (blk.startsWith("# ")) return <h2 key={i}>{blk.replace(/^#\s*/, "")}</h2>;
    return <p key={i}>{blk}</p>;
  });
};

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: allBlogs, isLoading, error } = useBlogs();
  const { scrollYProgress } = useScroll();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const unsub = scrollYProgress.on("change", (v) => setProgress(v));
    return () => unsub();
  }, [scrollYProgress]);

  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);

  if (isLoading || error) return null;

  const blog = allBlogs?.find((b) => b.slug === slug);

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-not-found">
        <div className="text-center">
          <h2 className="font-serif text-3xl mb-4">Article not found</h2>
          <button onClick={() => navigate("/")} className="btn-gold px-6 py-3 rounded-full text-sm">Back home</button>
        </div>
      </div>
    );
  }

  const related = (allBlogs || []).filter((b) => b.id !== blog.id).slice(0, 3);
  const url = typeof window !== "undefined" ? window.location.href : "";

  const share = (where) => {
    const text = encodeURIComponent(blog.title);
    const u = encodeURIComponent(url);
    const map = {
      twitter: `https://twitter.com/intent/tweet?url=${u}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    };
    if (where === "copy") {
      navigator.clipboard.writeText(url).then(() => toast.success("Link copied to clipboard"));
      return;
    }
    window.open(map[where], "_blank", "noopener,noreferrer");
  };

  return (
    <main className="pt-28 pb-20 relative" data-testid="blog-detail-page">
      {/* Reading progress */}
      <div className="fixed top-0 left-0 right-0 h-1 z-50 bg-transparent">
        <motion.div
          style={{ scaleX: progress, transformOrigin: "0%" }}
          className="h-full bg-gradient-to-r from-[hsl(var(--gold-deep))] via-[hsl(var(--gold))] to-[hsl(var(--gold-light))]"
          data-testid="reading-progress"
        />
      </div>

      <article className="container-x max-w-3xl">
        <button
          onClick={() => navigate(-1)}
          data-testid="blog-back"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[hsl(var(--gold-deep))] dark:hover:text-[hsl(var(--gold-light))] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" />{fmt(blog.publishedDate)}</span>
          <span className="inline-flex items-center gap-1.5"><Tag className="w-3 h-3" />{blog.tags ? blog.tags.split(",")[0].trim() : "General"}</span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mt-5 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
          data-testid="blog-title"
        >
          {blog.title}
        </motion.h1>

        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{blog.summary}</p>

        <div className="mt-10 rounded-2xl overflow-hidden glass p-2">
          <img src={getImageUrl(blog.featuredImageUrl)} alt={blog.title} className="w-full aspect-[16/9] object-cover rounded-xl" />
        </div>

        <div className="prose-luxury mt-12" data-testid="blog-content">
          {renderContent(blog.content)}
        </div>

        {/* Tags */}
        <div className="mt-14 flex flex-wrap gap-2">
          {blog.tags && blog.tags.split(",").map((t, i) => (
            <span key={i} className="px-3 py-1.5 rounded-full text-xs glass border border-[hsl(var(--border))]">{t.trim()}</span>
          ))}
        </div>

        {/* Share */}
        <div className="mt-10 glass rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4 grain">
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
            <span className="font-serif text-lg">Share this article</span>
          </div>
          <div className="flex items-center gap-2">
            {[
              { I: Twitter, k: "twitter" },
              { I: Linkedin, k: "linkedin" },
              { I: Facebook, k: "facebook" },
              { I: LinkIcon, k: "copy" },
            ].map(({ I, k }) => (
              <button key={k} onClick={() => share(k)} data-testid={`share-${k}`} className="w-10 h-10 rounded-full glass flex items-center justify-center hover:border-[hsl(var(--gold))] hover:text-[hsl(var(--gold-deep))] dark:hover:text-[hsl(var(--gold-light))] transition-all">
                <I className="w-4 h-4" />
              </button>
            ))}
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="container-x max-w-6xl mt-24">
          <div className="luxury-divider mb-10"><span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">More to read</span></div>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((b) => (
              <Link to={`/blog/${b.slug}`} key={b.id} className="group glass rounded-2xl overflow-hidden grain block">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={getImageUrl(b.featuredImageUrl)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1200" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{fmt(b.publishedDate)}</div>
                  <h3 className="mt-2 font-serif text-lg leading-snug group-hover:text-[hsl(var(--gold-deep))] dark:group-hover:text-[hsl(var(--gold-light))] transition-colors">{b.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
